import { parseISO } from "date-fns";
import {
  ArrowLeft,
  ChevronDown,
  Copy,
  Database,
  ExternalLink,
  FileText,
  GitCommitHorizontal,
  MessageSquarePlus,
} from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { SectionCard } from "~/components/section-card";
import { Badge, Button } from "~/components/ui";
import type { ChangeRequestDetailUI } from "~/features/changeRequests/types";
import { cn } from "~/lib/utils";
import { ChangeRequestItemSection } from "./ChangeRequestItemSection";
import { ChangeRequestReviewForm } from "./ChangeRequestReviewForm";
import { ChangeRequestReviewHistory } from "./ChangeRequestReviewHistory";
import { ChangeRequestStatusBadge } from "./ChangeRequestStatusBadge";
import { EvidenceCitation } from "./EvidenceCitation";

interface ChangeRequestDetailPresenterProps {
  detail: ChangeRequestDetailUI;
  variant: "page" | "peek";
  onBack: () => void;
  onCopy: (value: string) => void;
}

export function ChangeRequestDetailPresenter({ detail, variant, onBack, onCopy }: ChangeRequestDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";
  const titleSize = isPeek ? "text-16-bold" : "text-dewey-title";

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="min-w-0 space-y-1">
        {!isPeek && (
          <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
            <ArrowLeft />
            <span>{t("ui.back")}</span>
          </Button>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-14-regular text-fg-3">{t("feature.changeRequests.detail.titlePrefix")}</span>
            {detail.items.map((item, i) => {
              const recordPath =
                item.recordLink && item.recordId
                  ? `/${item.recordLink === "entity" ? "entities" : "relations"}/${item.recordId}`
                  : null;
              return (
                <span key={item.id} className="inline-flex items-baseline gap-1.5">
                  {i > 0 && (
                    <span aria-hidden className="text-fg-3">
                      ·
                    </span>
                  )}
                  <span className={cn("font-bold text-fg-1", titleSize)}>
                    {item.operationType} {item.modelTypeLabel}
                  </span>
                  {item.recordName && <span className={cn("text-fg-1", titleSize)}>{item.recordName}</span>}
                  {recordPath && item.recordName && (
                    <Link
                      to={recordPath}
                      className="self-center text-fg-link hover:underline"
                      aria-label={t("ui.viewInNewPage")}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  )}
                </span>
              );
            })}
          </h1>
          <ChangeRequestStatusBadge status={detail.status} />
        </div>
        <div className="flex items-center gap-2 text-12-regular text-fg-3">
          <span className="break-all tabular-nums">{detail.id}</span>
          <CopyButton onCopy={() => onCopy(detail.id)} label={t("ui.copyId")} />
        </div>
        <div className="flex flex-wrap gap-x-4 text-12-regular text-fg-3">
          <span>
            {t("model.common.createdAt")}: {t("date.long", { date: parseISO(detail.createdAt) })}
          </span>
          <span>
            {t("model.common.updatedAt")}: {t("date.long", { date: parseISO(detail.updatedAt) })}
          </span>
          <span>{detail.userName}</span>
        </div>
      </div>

      <SectionCard
        icon={FileText}
        title={t("model.changeRequest.modelName")}
        contentClassName="divide-y divide-dashed divide-border-default py-0 text-[13px]"
      >
        <Row label={t("model.changeRequest.composerKey")}>
          {detail.composerKey ? (
            <CopyableId value={detail.composerKey} onCopy={onCopy} label={t("ui.copyId")} />
          ) : (
            <span className="text-fg-3">—</span>
          )}
        </Row>
        <Row label={t("model.changeRequest.status")}>
          <ChangeRequestStatusBadge status={detail.status} />
        </Row>
        {detail.batchChangeRequestsJobId && (
          <Row label={t("model.batchChangeRequestsJob.modelName")}>
            <CopyableId value={detail.batchChangeRequestsJobId} onCopy={onCopy} label={t("ui.copyId")} />
          </Row>
        )}
        {detail.description && (
          <Row label={t("model.changeRequest.description")}>
            <span className="whitespace-pre-line text-fg-1">{detail.description}</span>
          </Row>
        )}
        <Row label={t("model.changeRequest.context")}>
          <ContextValue value={detail.context} />
        </Row>
        <Row label={t("model.common.createdAt")}>
          <span className="tabular-nums text-fg-1">{t("date.long", { date: parseISO(detail.createdAt) })}</span>
        </Row>
        <Row label={t("model.common.updatedAt")}>
          <span className="tabular-nums text-fg-1">{t("date.long", { date: parseISO(detail.updatedAt) })}</span>
        </Row>
      </SectionCard>

      <SourceInfoCard detail={detail} />

      {detail.items.map((item) => (
        <ChangeRequestItemSection key={item.id} item={item} />
      ))}

      <SectionCard
        icon={MessageSquarePlus}
        title={t("model.changeRequest.reviewComments")}
        contentClassName="space-y-4 text-[13px]"
      >
        <ChangeRequestReviewHistory reviews={detail.reviews} />
        <div className="border-t border-border-default pt-4">
          <ChangeRequestReviewForm
            changeRequestId={detail.id}
            hasNonCommentReviews={detail.reviews.some((r) => r.typeId !== "comment")}
          />
        </div>
      </SectionCard>

      {detail.commit && <CommitSection commit={detail.commit} />}
    </div>
  );
}

function SourceInfoCard({ detail }: { detail: ChangeRequestDetailUI }) {
  const { t } = useTranslation();
  const { source, evidences } = detail;
  const dipId = source.dataImportProject?.id ?? null;
  const dipPeek = (peekType: string, peekId: string): string | null =>
    dipId ? `/data-import-projects/${dipId}?peekType=${peekType}&peekId=${peekId}` : null;

  return (
    <SectionCard
      icon={Database}
      title={t("feature.changeRequests.detail.sourceInfo")}
      contentClassName="divide-y divide-dashed divide-border-default py-0 text-[13px]"
    >
      <Row label={t("model.evidence.modelNames")}>
        {evidences.length === 0 ? (
          <span className="text-fg-3">—</span>
        ) : (
          <div className="flex flex-col gap-2">
            {evidences.map((e) => (
              <Suspense
                key={e.id}
                fallback={<span className="break-all font-mono text-12-regular text-fg-3">{e.id}</span>}
              >
                <EvidenceCitation evidenceId={e.id} />
              </Suspense>
            ))}
          </div>
        )}
      </Row>
      <Row label={t("model.dataImportProject.modelName")}>
        {source.dataImportProject ? (
          <NameWithLink
            name={source.dataImportProject.name}
            to={`/data-import-projects/${source.dataImportProject.id}`}
            label={t("ui.view")}
          />
        ) : (
          <span className="text-fg-3">—</span>
        )}
      </Row>
      <Row label={t("model.dataSource.modelName")}>
        {source.dataSources.length === 0 ? (
          <span className="text-fg-3">—</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {source.dataSources.map((ds) => (
              <span key={ds.id} className="flex flex-wrap items-center gap-2">
                <NameWithLink name={ds.name} to={dipPeek("dataSource", ds.id)} label={t("ui.view")} />
                {ds.gsheetUrl && (
                  <a
                    href={ds.gsheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-fg-link hover:underline"
                  >
                    {t("feature.changeRequests.detail.spreadsheet")}
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                  </a>
                )}
              </span>
            ))}
          </div>
        )}
      </Row>
      <Row label={t("model.batchEvidencesJob.modelName")}>
        {source.batchEvidencesJobs.length === 0 ? (
          <span className="text-fg-3">—</span>
        ) : (
          <div className="flex flex-col gap-1">
            {source.batchEvidencesJobs.map((j) => (
              <NameWithLink
                key={j.id}
                name={j.name ?? j.id}
                to={dipPeek("batchEvidencesJob", j.id)}
                label={t("ui.view")}
              />
            ))}
          </div>
        )}
      </Row>
      <Row label={t("model.batchChangeRequestsJob.modelName")}>
        {source.batchChangeRequestsJob ? (
          <NameWithLink
            name={source.batchChangeRequestsJob.name ?? source.batchChangeRequestsJob.id}
            to={dipPeek("batchChangeRequestsJob", source.batchChangeRequestsJob.id)}
            label={t("ui.view")}
          />
        ) : (
          <span className="text-fg-3">—</span>
        )}
      </Row>
      <Row label={t("model.batchChangeRequestsJobRun.modelName")}>
        {source.jobRunId ? (
          <span className="break-all tabular-nums text-fg-1">{source.jobRunId}</span>
        ) : (
          <span className="text-fg-3">—</span>
        )}
      </Row>
    </SectionCard>
  );
}

function CommitSection({ commit }: { commit: NonNullable<ChangeRequestDetailUI["commit"]> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      icon={GitCommitHorizontal}
      title={t("feature.changeRequests.detail.commit")}
      action={
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("ui.toggle")}
          onClick={() => setOpen((v) => !v)}
        >
          <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
        </Button>
      }
    >
      {open ? (
        <div className="space-y-3 text-[13px]">
          <p className="tabular-nums text-fg-3">{t("date.long", { date: parseISO(commit.createdAt) })}</p>
          {commit.items.map((item) => (
            <pre
              key={item.changeRequestItemId}
              className="overflow-x-auto rounded-[var(--radius-sm)] bg-bg-subtle p-3 font-mono text-12-regular text-fg-1"
            >
              {JSON.stringify(item.properties, null, 2)}
            </pre>
          ))}
        </div>
      ) : (
        <p className="text-[13px] text-fg-3">
          {t("feature.changeRequests.detail.commitCollapsed", { count: commit.items.length })}
        </p>
      )}
    </SectionCard>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(7rem,11rem)_1fr] items-start gap-4 py-2.5">
      <span className="text-fg-3">{label}</span>
      <div className="min-w-0 text-fg-1">{children}</div>
    </div>
  );
}

function CopyButton({ onCopy, label }: { onCopy: () => void; label: string }) {
  return (
    <Button type="button" variant="ghost" size="icon" className="h-5 w-5 shrink-0" aria-label={label} onClick={onCopy}>
      <Copy className="h-3 w-3" />
    </Button>
  );
}

function CopyableId({ value, onCopy, label }: { value: string; onCopy: (v: string) => void; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="break-all font-mono text-12-regular text-fg-1">{value}</span>
      <CopyButton onCopy={() => onCopy(value)} label={label} />
    </span>
  );
}

/** A plain-text name followed by an external-link icon (only the icon is the link). */
function NameWithLink({ name, to, label }: { name: string; to: string | null; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-fg-1">{name}</span>
      {to && (
        <Link to={to} className="shrink-0 text-fg-link hover:text-primary-700" aria-label={label}>
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      )}
    </span>
  );
}

function ContextValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || (typeof value === "object" && Object.keys(value).length === 0)) {
    return <span className="text-fg-3">—</span>;
  }
  if (typeof value === "object") {
    return (
      <dl className="flex flex-col gap-0.5">
        {Object.entries(value as Record<string, unknown>).map(([key, val]) => (
          <div key={key} className="flex gap-2">
            <dt className="font-mono text-fg-3">{key}:</dt>
            <dd className="min-w-0 break-words text-fg-1">
              {typeof val === "object" ? JSON.stringify(val) : String(val)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span className="break-words text-fg-1">{String(value)}</span>;
}
